import { useMemo, useCallback } from "react";
import { useInsight } from "./insight-modal";
import type { GlossaryEntry } from "@shared/schema";

interface ArticleContentHighlighterProps {
  content: string;
  className?: string;
}

interface TextSegment {
  type: "text" | "term";
  content: string;
  term?: string;
  slug?: string;
}

export function ArticleContentHighlighter({ content, className }: ArticleContentHighlighterProps) {
  const { openInsight, glossaryEntries } = useInsight();

  const activeTerms = useMemo(() => {
    return glossaryEntries
      .filter(e => e.isActive)
      .sort((a, b) => b.term.length - a.term.length);
  }, [glossaryEntries]);

  const segments = useMemo(() => {
    if (!activeTerms.length) {
      return [{ type: "text" as const, content }];
    }

    const result: TextSegment[] = [];
    let remaining = content;

    while (remaining.length > 0) {
      let earliestMatch: { index: number; term: GlossaryEntry; matchedText: string } | null = null;

      for (const term of activeTerms) {
        const termPattern = new RegExp(`\\b(${escapeRegex(term.term)})\\b`, "gi");
        const match = termPattern.exec(remaining);
        
        if (match && (!earliestMatch || match.index < earliestMatch.index)) {
          earliestMatch = {
            index: match.index,
            term,
            matchedText: match[1],
          };
        }
      }

      if (!earliestMatch) {
        result.push({ type: "text", content: remaining });
        break;
      }

      if (earliestMatch.index > 0) {
        result.push({ type: "text", content: remaining.slice(0, earliestMatch.index) });
      }

      result.push({
        type: "term",
        content: earliestMatch.matchedText,
        term: earliestMatch.term.term,
        slug: earliestMatch.term.slug || undefined,
      });

      remaining = remaining.slice(earliestMatch.index + earliestMatch.matchedText.length);
    }

    return result;
  }, [content, activeTerms]);

  const handleTermClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, slug: string) => {
    e.preventDefault();
    openInsight(slug);
  }, [openInsight]);

  return (
    <span className={className}>
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return <span key={i}>{segment.content}</span>;
        }

        if (segment.slug) {
          return (
            <a
              key={i}
              href={`/insights/${segment.slug}`}
              onClick={(e) => handleTermClick(e, segment.slug!)}
              className="inline underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 text-inherit hover:decoration-solid hover:decoration-primary/70 hover:text-primary transition-colors cursor-pointer"
              data-testid={`glossary-term-${segment.term}`}
            >
              {segment.content}
            </a>
          );
        }

        return (
          <span
            key={i}
            className="inline underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 text-inherit cursor-default"
            title={`Term: ${segment.term}`}
            data-testid={`glossary-term-${segment.term}`}
          >
            {segment.content}
          </span>
        );
      })}
    </span>
  );
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface HighlightedHTMLContentProps {
  html: string;
  className?: string;
}

export function HighlightedHTMLContent({ html, className }: HighlightedHTMLContentProps) {
  const { openInsight, glossaryEntries } = useInsight();

  const activeTerms = useMemo(() => {
    return glossaryEntries
      .filter(e => e.isActive)
      .sort((a, b) => b.term.length - a.term.length);
  }, [glossaryEntries]);

  const highlightedHtml = useMemo(() => {
    if (!activeTerms.length) return html;

    let result = html;
    
    const tempMarkers: Map<string, string> = new Map();
    let markerIndex = 0;

    for (const term of activeTerms) {
      const termPattern = new RegExp(`(?<![\\w>])\\b(${escapeRegex(term.term)})\\b(?![\\w<])`, "gi");
      
      result = result.replace(termPattern, (match) => {
        const marker = `__GLOSSARY_TERM_${markerIndex++}__`;
        if (term.slug) {
          tempMarkers.set(marker, `<a href="/insights/${term.slug}" class="glossary-term-link" data-slug="${term.slug}" data-term="${term.term}">${match}</a>`);
        } else {
          tempMarkers.set(marker, `<span class="glossary-term-no-link" title="Term: ${term.term}">${match}</span>`);
        }
        return marker;
      });
    }

    tempMarkers.forEach((replacement, marker) => {
      result = result.replace(marker, replacement);
    });

    return result;
  }, [html, activeTerms]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("glossary-term-link")) {
      const slug = target.getAttribute("data-slug");
      if (slug) {
        e.preventDefault();
        openInsight(slug);
      }
    }
  }, [openInsight]);

  return (
    <div
      className={className}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
    />
  );
}
